import { useContext } from 'react'

import { useLatestEntity } from '@domain-group/react-hooks'

import { formatDate, launchModal } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import { destructiveActions } from '@functions/activity'
import { baseURL } from '@functions/api'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ActivityModel, ActivityRequestOptions } from '@domain-group/realhub-backend-api-types'

import type { ModuleState } from '@redux/modules/types'

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

export const generateUrls = (
  activity?: Partial<ActivityModel>,
  options?: GenerateUrlsOptions,
) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    activitiesIndexUrl: `#/${prefixRoute}/activity`,
    // @ts-expect-error TS18048
    exportActivity: `${baseURL}/activities/${activity.id}/export.json`,
  }
}

// Return related entities
export const useRelations = (entity: Partial<ActivityModel> = {}) => {
  const { owner_id, owner_type } = entity

  const { users } = useSelector(reduxState => reduxState.entities)

  const user = owner_type === 'User' && owner_id && users[owner_id] ? users[owner_id] : {}

  return {
    user,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ActivityRequestOptions,
}

function useActivity(initEntity: Partial<ActivityModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: activity }: { entity: ActivityModel} = useLatestEntity(initEntity, 'activities')
  const { id } = activity

  useReduxAction(
    'activities',
    'loadActivity',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { callbacks } = useContext(PageContext)

  const { user } = useRelations(activity)

  const { loading, loadedIds } = useSelector(reduxState => reduxState.activities)

  const {
    action, parameters, recipient_id, recipient_type, trackable, trackable_id, trackable_type,
  } = activity

  const { changes = {} } = parameters || {}

  const createdAt = activity?.dates?.created?.date_time || ''
  const formattedDateLong = formatDate(createdAt, 'dd/MM/yyyy HH:mm', "ccc d LLL yyyy 'at' ta")
  const formattedDateShort = formatDate(createdAt, 'dd/MM/yyyy HH:mm', "d/MM/yy 'at' h:mma")

  const recipientIsTrackable = recipient_id === trackable_id && recipient_type === trackable_type
  const activityHasReason = !!trackable?.reason

  return {
    activity,
    activityHasReason,
    callbacks: {
      manageActivity: () => launchModal({
        callbacks,
        modalKey: 'ManageActivityModal',
        payload: { activity },
      }),
    },
    formattedDateLong,
    formattedDateShort,
    hasChanges: !!Object.keys(changes).length,
    isDestructive: destructiveActions.some(item => action?.includes(item)) || parameters?.deleted,
    loading: loading && !loadedIds.includes(activity.id),
    recipientIsTrackable,
    urls: generateUrls(activity),
    user,
  }
}

export default useActivity
